import { useEffect, useState, useCallback, useMemo } from "react";
import { addDays, format, startOfWeek, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { todayStr, STATUS_STYLES } from "../lib/helpers";
import { PageHeader } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import AppointmentDrawer from "../components/AppointmentDrawer";
import AppointmentForm from "../components/AppointmentForm";

const START = 7 * 60, END = 21 * 60, ROW = 14;
const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const fromMin = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function ApptCard({ a, onOpen, style, compact }) {
  return (
    <button draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", a.id)} onClick={() => onOpen(a.id)}
      data-testid={`agenda-appt-${a.id}`} style={style}
      className={`text-left rounded-md border px-2 py-1 text-xs overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-card ${compact ? "w-full mb-1.5" : "absolute left-1 right-1"}`}>
      <div className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: a.service_color || "hsl(var(--primary))" }} />
        <span className="font-mono">{a.start_time}</span>
        <span className={`ml-auto h-2 w-2 rounded-full border ${STATUS_STYLES[a.status]}`} title={a.status} />
      </div>
      <div className="font-semibold truncate">{a.client_name}</div>
      <div className="truncate text-muted-foreground">{a.service_name}{compact ? ` · ${a.professional_name}` : ""}</div>
    </button>
  );
}

function DayGrid({ date, pros, appts, onOpen, onCreate, onDrop }) {
  const [over, setOver] = useState(null);
  const rows = (END - START) / 15;
  return (
    <div className="overflow-x-auto rounded-[var(--radius)] border bg-card" data-testid="agenda-day-grid">
      <div className="grid min-w-[640px]" style={{ gridTemplateColumns: `64px repeat(${Math.max(pros.length, 1)}, minmax(170px, 1fr))` }}>
        <div className="sticky left-0 bg-card border-b" />
        {pros.map((p) => (
          <div key={p.id} className="border-b border-l px-3 py-3 text-sm font-semibold flex items-center gap-2" data-testid={`agenda-col-${p.id}`}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />{p.name}
          </div>
        ))}
        <div className="relative sticky left-0 bg-card z-10">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} style={{ height: ROW }} className="text-[10px] text-muted-foreground pr-2 text-right">
              {i % 4 === 0 ? fromMin(START + i * 15) : ""}
            </div>
          ))}
        </div>
        {pros.map((p) => (
          <div key={p.id} className="relative border-l">
            {Array.from({ length: rows }).map((_, i) => {
              const key = `${p.id}-${i}`;
              return (
                <div key={i} style={{ height: ROW }} data-testid={i === 8 ? `agenda-cell-${p.id}-0900` : undefined}
                  className={`agenda-cell cursor-pointer ${i % 4 === 0 ? "border-t" : ""} ${over === key ? "drop" : ""}`}
                  onClick={() => onCreate(p.id, fromMin(START + i * 15))}
                  onDragOver={(e) => { e.preventDefault(); setOver(key); }} onDragLeave={() => setOver(null)}
                  onDrop={(e) => { e.preventDefault(); setOver(null); onDrop(e.dataTransfer.getData("text/plain"), p.id, date, fromMin(START + i * 15)); }} />
              );
            })}
            {appts.filter((a) => a.professional_id === p.id && a.status !== "cancelled").map((a) => {
              const top = ((toMin(a.start_time) - START) / 15) * ROW;
              return <ApptCard key={a.id} a={a} onOpen={onOpen} style={{ top, height: Math.max((a.duration / 15) * ROW - 2, 30) }} />;
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Agenda() {
  const { staff, locationId, user, can } = useApp();
  const { t } = useT();
  const [date, setDate] = useState(todayStr());
  const [view, setView] = useState("day");
  const [appts, setAppts] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [create, setCreate] = useState(null);

  const range = useMemo(() => {
    if (view === "day") return [date, date];
    const s = startOfWeek(parseISO(date), { weekStartsOn: 1 });
    return [format(s, "yyyy-MM-dd"), format(addDays(s, 6), "yyyy-MM-dd")];
  }, [date, view]);

  const load = useCallback(() => {
    api.get("/appointments", { params: { date_from: range[0], date_to: range[1], location_id: locationId || undefined } }).then((r) => setAppts(r.data));
  }, [range, locationId]);
  useEffect(() => { load(); }, [load]);

  const pros = user.role === "professional" ? staff.filter((s) => s.id === user.id) :
    staff.filter((s) => s.active && ["professional", "owner"].includes(s.role) && s.location_ids?.length && (!locationId || s.location_ids.includes(locationId)));

  const shift = (n) => setDate(format(addDays(parseISO(date), view === "day" ? n : n * 7), "yyyy-MM-dd"));
  const onDrop = async (id, proId, d, time) => {
    const a = appts.find((x) => x.id === id);
    if (!a || !can("agenda.write")) return;
    try {
      await api.put(`/appointments/${id}`, { client_id: a.client_id, service_id: a.service_id, professional_id: proId, location_id: a.location_id, date: d, start_time: time, notes: a.notes });
      toast.success(`Moved to ${time}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onCreate = (proId, time) => can("agenda.write") && setCreate({ professional_id: proId, start_time: time, date });

  const days = view === "week" ? Array.from({ length: 7 }).map((_, i) => format(addDays(parseISO(range[0]), i), "yyyy-MM-dd")) : [];
  return (
    <div>
      <PageHeader eyebrow={format(parseISO(date), "EEEE d MMMM yyyy")} title={t("agenda")}>
        <div className="flex rounded-full border p-1" data-testid="agenda-view-toggle">
          {["day", "week"].map((v) => (
            <button key={v} data-testid={`agenda-view-${v}`} onClick={() => setView(v)}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors ${view === v ? "bg-primary text-primary-foreground" : ""}`}>{t(v)}</button>
          ))}
        </div>
        <Button variant="outline" size="icon" data-testid="agenda-prev" onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
        <Input type="date" className="w-40" data-testid="agenda-date-input" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
        <Button variant="outline" size="icon" data-testid="agenda-next" onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="ghost" data-testid="agenda-today" onClick={() => setDate(todayStr())}>{t("dashboard")}</Button>
        {can("agenda.write") && <Button className="rounded-full" data-testid="agenda-new-btn" onClick={() => setCreate({ date })}>{t("new_appointment")}</Button>}
      </PageHeader>

      {view === "day" ? (
        <DayGrid date={date} pros={pros} appts={appts} onOpen={setOpenId} onCreate={onCreate} onDrop={onDrop} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="agenda-week-grid">
          {days.map((d) => (
            <div key={d} className={`rounded-[var(--radius)] border bg-card p-2 min-h-[220px] ${d === todayStr() ? "ring-2 ring-primary/40" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { const a = appts.find((x) => x.id === e.dataTransfer.getData("text/plain")); a && onDrop(a.id, a.professional_id, d, a.start_time); }}>
              <button className="w-full text-left mb-2 px-1" data-testid={`agenda-week-day-${d}`} onClick={() => { setDate(d); setView("day"); }}>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{format(parseISO(d), "EEE")}</div>
                <div className="font-display text-xl font-bold">{format(parseISO(d), "d")}</div>
              </button>
              {appts.filter((a) => a.date === d && a.status !== "cancelled").map((a) => <ApptCard key={a.id} a={a} onOpen={setOpenId} compact />)}
            </div>
          ))}
        </div>
      )}
      <AppointmentDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />
      <AppointmentForm open={!!create} onOpenChange={(o) => !o && setCreate(null)} initial={create || {}} onSaved={load} />
    </div>
  );
}
